import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Modal from '../ui/Modal';
import { SubscriptionService } from '../../api/generated/services/SubscriptionService';
import { ApiError } from '../../api/generated/core/ApiError';
import { helper } from '../../utils/helper';
import { addNotification } from '../../store/slices/notificationSlice';
import { fetchUserLicenses } from '../../store/slices/licenseSlice';
import { AppDispatch } from '../../store/store';

interface LicenseCancelScheduledChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Active subscription A whose pending downgrade should be cancelled. */
  subscriptionId: string;
  /** Display name of subscription A's product (for the modal copy). */
  currentProductName: string;
  /** Display name of the scheduled product B (for the modal copy). */
  scheduledProductName: string;
  /** Optional callback after a successful cancel + refresh. */
  onSuccess?: () => void;
}

const LicenseCancelScheduledChangeModal: React.FC<LicenseCancelScheduledChangeModalProps> = ({
  isOpen,
  onClose,
  subscriptionId,
  currentProductName,
  scheduledProductName,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await SubscriptionService.deleteAppSubscriptionCancelScheduledChange(subscriptionId);

      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('subscription_cancel_scheduled_toast_success'),
        autoHideDuration: 5000,
      }));

      dispatch(fetchUserLicenses({ page: 1, pageSize: 100, silent: true }));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to cancel scheduled change:', error);

      let errorMessage = t('subscription_cancel_scheduled_toast_failed');
      if (error instanceof ApiError) {
        const parsedBody = helper.parseApiErrorBody(error);
        if (parsedBody && parsedBody.error) {
          errorMessage = parsedBody.error;
        }
      }

      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: errorMessage,
        autoHideDuration: 8000,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('subscription_cancel_scheduled_modal_title')}
      hideCloseButton={true}
      isCentered={true}
    >
      <div className="modal-success">
        <div className="modal-success-text">
          {t('subscription_cancel_scheduled_modal_text', {
            plannedPlan: scheduledProductName,
            currentPlan: currentProductName,
          })}
        </div>
        <div className="modal-buttons">
          <button
            className="modal-button cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            {t('subscription_cancel_scheduled_modal_back')}
          </button>
          <button
            className="modal-button confirm"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading
              ? t('subscription_cancel_scheduled_processing')
              : t('subscription_cancel_scheduled_modal_confirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default LicenseCancelScheduledChangeModal;
